// frontend/apps/demo/piwa-portfolio/src/theme/prehistorik-theme.ts

import { ColorVariant, Size } from '../types';

export const prehistorikTheme = {
  // Color System - Based on Prehistorik Man SNES palette
  colors: {
    // Stone/Rock colors
    stone: {
      900: '#201919', // obsidian-900 - text/outlines
      800: '#2A2220',
      700: '#4D3F3A', // stone-700 - rock lines/titles  
      600: '#665752',
      500: '#7A6A63',
      400: '#8B766E', // stone-400 - rock fill
      300: '#A69389',
      200: '#C2B0A4',
      100: '#DDD0C4'
    },
    
    // Moss/Jungle greens
    moss: {
      700: '#1F5A2B',
      600: '#2E6A39', // moss-600 - primary actions/success
      500: '#3E7A49',
      400: '#4E8A59',
      300: '#6FB876', // moss-300 - hovers
      200: '#90C896',
      100: '#B0D8B6'
    },
    
    // Sky blues
    sky: {
      700: '#2B5A8C',
      600: '#4A7AAC',
      500: '#6A9ACC',
      400: '#8ABAEC',
      300: '#B6D6F5', // sky-300 - backgrounds
      200: '#D6E6FA',
      100: '#E7F2FF' // sky-100 - panels
    },
    
    // Sunset/Fire oranges
    sunset: {
      700: '#B45A0C',
      600: '#D46A1C',
      500: '#F4A24C', // sunset-500 - highlights
      400: '#F6B26C',
      300: '#F8C28C',
      200: '#FAD2AC',
      100: '#FCE2CC'
    },
    
    // Berry/Heart reds  
    berry: {
      700: '#8C1E2A',
      600: '#C42E3A', // berry-600 - alerts/hearts
      500: '#D44E5A',
      400: '#E46E7A',
      300: '#EC8E9A',
      200: '#F4AEBA',
      100: '#FCCEDA'
    },
    
    // Bone/UI surfaces
    bone: {
      400: '#C6B498',
      300: '#D6C4A8',
      200: '#F6E4C8', // bone-200 - surfaces
      100: '#FAF2E8',
      50: '#FFFCF8'
    },
    
    // Cave/Crystal purples
    cave: {
      dark: '#2A1C3C',
      shadow: '#3A2C4C',
      deep: '#4A3C5C',
      crystal: '#7A6C9C',
      glow: '#9A8CBC',
      light: '#BAACDC'
    },
    
    // Ice/Winter blues
    ice: {
      dark: '#1C3C5C',
      mid: '#2C4C6C',
      cold: '#3C5C7C',
      light: '#5C7C9C',
      bright: '#7C9CBC',
      pale: '#9CBCDC'
    },
    
    // Volcano/Lava
    volcano: {
      dark: '#3C1C0C',
      glow: '#5C2C1C',
      hot: '#7C3C2C',
      lava: '#9C4C3C',
      ember: '#BC5C4C',
      ash: '#DC7C6C'
    },
    
    // Jungle biome
    jungle: {
      dark: '#0C2C1C',
      deep: '#1C3C2C',
      mid: '#2C4C3C',
      canopy: '#3C5C4C',
      bright: '#4C6C5C',
      light: '#5C7C6C'
    },
    
    // Village/Shop browns
    village: {
      dark: '#3C2C1C',
      wood: '#4C3C2C',
      leather: '#5C4C3C',
      tent: '#6C5C4C',
      rope: '#7C6C5C',
      sand: '#8C7C6C'
    }
  },
  
  // Typography
  fonts: {
    // Display fonts for headings (comic/caveman style)
    display: "'Bangers', 'Luckiest Guy', 'Chewy', cursive",
    // UI fonts for body text
    ui: "'Manrope', 'Outfit', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    // Pixel font for retro elements
    pixel: "'Press Start 2P', 'VT323', monospace",
    // Monospace for code
    mono: "'Fira Code', 'Cascadia Code', 'Courier New', monospace"
  },
  
  // Font sizes (in rem)
  fontSizes: {
    xs: '0.75rem',   // 12px
    sm: '0.875rem',  // 14px
    base: '1rem',    // 16px
    lg: '1.125rem',  // 18px
    xl: '1.25rem',   // 20px
    '2xl': '1.5rem', // 24px
    '3xl': '2rem',   // 32px
    '4xl': '2.5rem', // 40px
    '5xl': '3rem',   // 48px
  },
  
  // Spacing (in px)
  spacing: {
    xs: '6px',
    sm: '10px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    '2xl': '48px',
    '3xl': '64px',
    '4xl': '96px',
  },
  
  // Border radius
  radius: {
    sm: '6px',
    md: '12px',
    lg: '18px',
    slab: '24px', // For stone slab effect
    round: '50%',
  },
  
  // Shadows (comic book style with dark outlines)
  shadows: {
    // Hard shadows for that SNES feel
    sun: '0 4px 0 #201919',
    float: '0 10px 20px rgba(32,25,25,.25)',
    stone: '0 6px 0 #201919, 0 12px 24px rgba(32,25,25,.3)',
    slab: '0 8px 0 #201919, 0 16px 32px rgba(32,25,25,.4)',
    inner: 'inset 0 2px 4px rgba(32,25,25,.5)',
    
    // Colored shadows for special effects
    moss: '0 4px 0 #1F5A2B, 0 8px 16px rgba(31,90,43,.3)',
    lava: '0 4px 0 #7C3C2C, 0 8px 16px rgba(124,60,44,.5)',
    ice: '0 4px 0 #1C3C5C, 0 8px 16px rgba(28,60,92,.3)',
  },
  
  // Borders (thick outlines for speech bubble effect)
  borders: {
    ui: '2px solid #201919',
    thick: '3px solid #201919',
    heavy: '4px solid #201919',
    dashed: '2px dashed #201919',
    dotted: '2px dotted #201919',
  },
  
  // Animation timings
  transitions: {
    fast: '120ms ease',
    base: '200ms ease',
    slow: '300ms ease',
    bounce: '400ms cubic-bezier(0.68, -0.55, 0.265, 1.55)',
    shake: '100ms linear',
  },
  
  // Z-index layers
  zIndex: {
    background: -1,
    base: 0,
    dropdown: 100,
    sticky: 200,
    overlay: 300,
    modal: 400,
    popover: 500,
    tooltip: 600,
    toast: 700,
  },
  
  // Breakpoints
  breakpoints: {
    xs: '480px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
  
  // Special effects
  effects: {
    // Pixel dithering for 16-bit feel
    dither: 'url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAIAAAACCAYAAABytg0kAAAAGklEQVQIHWNgYGD4z8DAwMAw6t8oHwUfBQDCCgEBAAAA)',
    // Scanlines for retro CRT effect
    scanlines: 'repeating-linear-gradient(0deg, rgba(0,0,0,.15), rgba(0,0,0,.15) 1px, transparent 1px, transparent 2px)',
    // Noise texture
    noise: 'url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAGklEQVQIHWNgYGD4z8DAwMAYw8DEgAYYKTgHAEjCA/8QpGttAAAAAElFTkSuQmCC)',
  },
  
  // Game-specific tokens
  game: {
    // HUD colors
    hudBg: 'rgba(32, 25, 25, 0.9)',
    hudBorder: '#4D3F3A',
    hudText: '#F6E4C8',
    
    // Power bar colors
    shoutEmpty: '#4D3F3A',
    shoutFilling: '#F4A24C',
    shoutFull: '#6FB876',
    shoutFlash: '#FAD2AC',
    
    // Life/hearts
    heartFull: '#C42E3A',
    heartEmpty: '#4D3F3A',
    heartDamage: '#FF6E7A',
    
    // Collectibles
    boneCoin: '#F6E4C8',
    boneShine: '#FFFCF8',
    
    // Status effects
    poisoned: '#7A6C9C',
    frozen: '#5C7C9C',
    burning: '#BC5C4C',
    powered: '#6FB876',
  },
};

// CSS classes for quick application
export const prehistorikClasses = {
  // Text styles
  'text-display': 'font-display text-bone-200',
  'text-ui': 'font-ui text-bone-200',
  'text-pixel': 'font-pixel text-bone-200',
  
  // Backgrounds
  'bg-stone': 'bg-gradient-to-br from-stone-800 to-stone-700',
  'bg-moss': 'bg-gradient-to-br from-moss-700 to-moss-600',
  'bg-sky': 'bg-gradient-to-br from-sky-500 to-sky-300',
  'bg-sunset': 'bg-gradient-to-br from-sunset-600 to-sunset-500',
  'bg-cave': 'bg-gradient-to-br from-cave-dark to-cave-shadow',
  'bg-ice': 'bg-gradient-to-br from-ice-dark to-ice-mid',
  'bg-volcano': 'bg-gradient-to-br from-volcano-dark to-volcano-glow',
  'bg-jungle': 'bg-gradient-to-br from-jungle-dark to-jungle-mid',
  
  // Borders
  'border-stone': 'border-4 border-stone-700',
  'border-moss': 'border-4 border-moss-600',
  'border-sunset': 'border-4 border-sunset-600',
  'border-berry': 'border-4 border-berry-700',
  'border-ice': 'border-4 border-ice-dark',
  'border-cave': 'border-4 border-cave-dark',
  
  // Shadows
  'shadow-stone': 'shadow-[0_6px_0_#201919,0_12px_24px_rgba(32,25,25,.3)]',
  'shadow-slab': 'shadow-[0_8px_0_#201919,0_16px_32px_rgba(32,25,25,.4)]',
  'shadow-sun': 'shadow-[0_4px_0_#201919]',
  
  // Rounded corners
  'rounded-stone': 'rounded-[12px]',
  'rounded-slab': 'rounded-[24px]',
  
  // Animations
  'animate-bounce-slow': 'animate-[bounce_2s_ease-in-out_infinite]',
  'animate-pulse-slow': 'animate-[pulse_3s_ease-in-out_infinite]',
  'animate-shake': 'animate-[shake_0.2s_ease-in-out_infinite]',
  'animate-float': 'animate-[float_3s_ease-in-out_infinite]',
};

// Tailwind config extension
export const tailwindPrehistorikConfig = {
  theme: {
    extend: {
      colors: prehistorikTheme.colors,
      fontFamily: {
        display: ['Bangers', 'cursive'],
        ui: ['Manrope', 'sans-serif'],
        pixel: ['"Press Start 2P"', 'monospace'],
      },
      borderRadius: prehistorikTheme.radius,
      spacing: prehistorikTheme.spacing,
      animation: {
        shake: 'shake 0.2s ease-in-out infinite',
        float: 'float 3s ease-in-out infinite',
        'bounce-slow': 'bounce 2s ease-in-out infinite',
      },
      keyframes: {
        shake: {
          '0%, 100%': { transform: 'translateX(0)' },
          '25%': { transform: 'translateX(-4px)' },
          '75%': { transform: 'translateX(4px)' },
        },
        float: {
          '0%, 100%': { transform: 'translateY(0)' },
          '50%': { transform: 'translateY(-10px)' },
        },
      },
    },
  },
};

// UI Theme Configuration - Component class mappings
// Color mappings based on Prehistorik theme
export const colorClasses: Record<ColorVariant, {
  bg: string;
  bgGradient: string;
  border: string;
  text: string;
  hover: string;
}> = {
  stone: {
    bg: 'bg-stone-700',
    bgGradient: 'bg-gradient-to-br from-stone-800 to-stone-700',
    border: 'border-stone-600',
    text: 'text-stone-400',
    hover: 'hover:bg-stone-600 hover:border-stone-500'
  },
  moss: {
    bg: 'bg-moss-600',
    bgGradient: 'bg-gradient-to-br from-moss-700 to-moss-600',
    border: 'border-moss-600',
    text: 'text-moss-400',
    hover: 'hover:bg-moss-500 hover:border-moss-500'
  },
  sky: {
    bg: 'bg-sky-500',
    bgGradient: 'bg-gradient-to-br from-sky-500/20 to-sky-300/20',
    border: 'border-sky-500',
    text: 'text-sky-300',
    hover: 'hover:bg-sky-400 hover:border-sky-400'
  },
  sunset: {
    bg: 'bg-sunset-500',
    bgGradient: 'bg-gradient-to-br from-sunset-600/30 to-sunset-500/30',
    border: 'border-sunset-600',
    text: 'text-sunset-400',
    hover: 'hover:bg-sunset-400 hover:border-sunset-500'
  },
  berry: {
    bg: 'bg-berry-600',
    bgGradient: 'bg-gradient-to-br from-berry-700/20 to-berry-600/20',
    border: 'border-berry-700',
    text: 'text-berry-400',
    hover: 'hover:bg-berry-500 hover:border-berry-600'
  },
  cave: {
    bg: 'bg-cave-dark',
    bgGradient: 'bg-gradient-to-br from-cave-dark/50 to-cave-shadow/50',
    border: 'border-cave-dark',
    text: 'text-cave-crystal',
    hover: 'hover:bg-cave-shadow hover:border-cave-crystal'
  },
  ice: {
    bg: 'bg-ice-dark',
    bgGradient: 'bg-gradient-to-br from-ice-dark/30 to-ice-mid/30',
    border: 'border-ice-dark',
    text: 'text-ice-light',
    hover: 'hover:bg-ice-mid hover:border-ice-light'
  },
  volcano: {
    bg: 'bg-volcano-glow',
    bgGradient: 'bg-gradient-to-br from-volcano-dark to-volcano-glow',
    border: 'border-volcano-lava',
    text: 'text-volcano-ember',
    hover: 'hover:bg-volcano-hot hover:border-volcano-ember'
  },
  jungle: {
    bg: 'bg-jungle-mid',
    bgGradient: 'bg-gradient-to-br from-jungle-dark to-jungle-mid',
    border: 'border-jungle-dark',
    text: 'text-jungle-bright',
    hover: 'hover:bg-jungle-light hover:border-jungle-bright'
  },
  bone: {
    bg: 'bg-bone-200',
    bgGradient: 'bg-gradient-to-br from-bone-300 to-bone-200',
    border: 'border-bone-300',
    text: 'text-bone-200',
    hover: 'hover:bg-bone-100 hover:border-bone-200'
  }
};

// Size mappings
export const sizeClasses: Record<Size, {
  text: string;
  padding: string;
  height: string;
  iconSize: string;
}> = {
  xs: {
    text: 'text-xs',
    padding: 'px-2 py-1',
    height: 'h-6',
    iconSize: 'w-3 h-3'
  },
  sm: {
    text: 'text-sm',
    padding: 'px-3 py-1.5',
    height: 'h-8',
    iconSize: 'w-4 h-4'
  },
  md: {
    text: 'text-base',
    padding: 'px-4 py-2',
    height: 'h-10',
    iconSize: 'w-5 h-5'
  },
  lg: {
    text: 'text-lg',
    padding: 'px-5 py-2.5',
    height: 'h-12',
    iconSize: 'w-6 h-6'
  },
  xl: {
    text: 'text-xl',
    padding: 'px-6 py-3',
    height: 'h-14',
    iconSize: 'w-7 h-7'
  }
};

// Common shadow styles
export const shadowClasses = {
  none: '',
  stone: 'shadow-stone',
  stoneLg: 'shadow-stone-lg',
  slab: 'shadow-slab',
  float: 'shadow-float',
  inner: 'shadow-inner'
};

// Common border styles
export const borderClasses = {
  none: '',
  thin: 'border-2',
  medium: 'border-3',
  thick: 'border-4',
  heavy: 'border-[5px]'
};

// Common rounded styles
export const roundedClasses = {
  none: '',
  sm: 'rounded',
  md: 'rounded-stone',
  lg: 'rounded-slab',
  full: 'rounded-full'
};

// Re-export the proper cn utility that uses clsx and twMerge
export { cn } from '../utils/cn';